import React, { useEffect } from 'react'
import styles from './Dashboard.module.css'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { getInvoicesByUser } from '../../actions/invoiceActions'
import Empty from '../svgIcons/Empty'
// Revenue analytics imports
import FiltersBar from "../analytics/FiltersBar";
import RevenueTrendChart from "../analytics/RevenueTrendChart";
import LeaderboardList from "../analytics/LeaderboardList";
import PaymentList from "../analytics/PaymentList";
import CustomerInvoicesCard from "../analytics/CustomerInvoicesCard";
import FlaggedInvoicesCard from "../analytics/FlaggedInvoicesCard";
import OverdueInvoicesCard from "../analytics/OverdueInvoicesCard";
import OutstandingInvoicesCard from "../analytics/OutstandingInvoicesCard";
import RecentPaymentsCard from "../analytics/RecentPaymentsCard";
import useRevenueAnalytics from "../../hooks/useRevenueAnalytics";
import TaxSummaryTable from "../analytics/TaxSummaryTable";


// MUI v4 helpers (adjust if you already import these elsewhere)
import { Box, Grid, ButtonGroup, Button } from "@material-ui/core";

// import Donut from './Donut'
import moment from 'moment'
import Spinner from '../Spinner/Spinner'
import Chart from './Chart'
import useAuthUser from '../../hooks/useAuthUser'


const Dashboard = () => {

    const location = useLocation()
    const history = useHistory()
    const dispatch = useDispatch()
    const authData = useAuthUser()
    const { invoices, isLoading } = useSelector((state) => state?.invoices)
    // const unpaid = invoices?.filter((invoice) => (invoice.status === 'Unpaid') || (invoice.status === 'Partial'))
    const overDue = invoices?.filter((invoice) => (invoice.dueDate <= new Date().toISOString()) && (invoice.status === 'Unpaid' || invoice.status === 'Partial' ))
    const currYear = new Date().getFullYear();
    // ----- Revenue widget controls -----
    const [mode, setMode] = React.useState("cash");                     // 'cash' | 'accrual'
    const [accrualField, setAccrualField] = React.useState("receivedByDate");
    const [rangePreset, setRangePreset] = React.useState("30d");         // '30d' | '90d' | 'ytd'
    const [useCustom, setUseCustom] = React.useState(false);
    const [fromCustom, setFromCustom] = React.useState("");
    const [toCustom, setToCustom] = React.useState("");
    const [statusIn, setStatusIn] = React.useState(["Paid","Partial","Unpaid"]);
    const [topProductMetric, setTopProductMetric] = React.useState("revenue");

    const currencyFmt = (v, code = "USD") =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: code, maximumFractionDigits: 0 }).format(v || 0);

    const now = new Date();
    const { fromISO, toISO, grp } = React.useMemo(() => {
    if (useCustom && fromCustom && toCustom) {
        const start = new Date(fromCustom + "T00:00:00");
        const end = new Date(new Date(toCustom).getTime() + 24*60*60*1000 - 1);
        const isYTD = new Date(fromCustom).toDateString() === new Date(new Date().getFullYear(), 0, 1).toDateString();
        return { fromISO: start.toISOString(), toISO: end.toISOString(), grp: isYTD ? "month" : "day" };
    }
    let start;
    if (rangePreset === "30d") start = new Date(now.getTime() - 29*864e5);
    else if (rangePreset === "90d") start = new Date(now.getTime() - 89*864e5);
    else start = new Date(now.getFullYear(), 0, 1);
    return { fromISO: start.toISOString(), toISO: new Date(now.getTime()+1).toISOString(), grp: rangePreset === "ytd" ? "month" : "day" };
    }, [useCustom, fromCustom, toCustom, rangePreset]);

    // Fetch analytics
    const { data, loading, error } = useRevenueAnalytics({
    endpoint: "/api/analytics/revenue",
    mode,
    accrualField,
    fromISO,
    toISO,
    grp,
    statusIn
    });

    // Derive view models
    const series = (data?.series ?? []).map(d => ({ ...d, period: new Date(d.period) }));
    const paymentsByInvoice = data?.paymentsByInvoice ?? [];
    const accrualInvoices = data?.accrualInvoices ?? [];
    const timelineEntries = mode === "accrual" ? accrualInvoices : paymentsByInvoice;

    const topProductsRows = React.useMemo(() => {
    const list = (data?.topProducts ?? []).map(p => ({
        name: p.name || p._id,
        id: p._id,
        revenue: Number(p.revenue) || 0,
        qty: Number(p.qty) || 0,
        profit: typeof p.margin === "number" ? p.margin : 0,
        margin: p.margin,
    }));
    const metricKey = topProductMetric === "quantity" ? "qty" : topProductMetric === "profit" ? "profit" : "revenue";
    return list
        .sort((a, b) => (b[metricKey] || 0) - (a[metricKey] || 0))
        .map(item => ({
            left: item.name,
            right: metricKey === "qty" ? Math.round(item.qty) : item[metricKey],
            sub: `Qty ${Math.round(item.qty)}${typeof item.margin === "number" ? ` • Gross Margin ${currencyFmt(item.margin, "USD")}` : ""}`,
            meta: { modelNumber: item.id }
        }));
    }, [data?.topProducts, topProductMetric]);
    const topCustomersRows = (data?.topCustomers ?? []).map(c => {
      const locDetails = (c.locations || [])
        .filter(l => l?.location)
        .map(l => `${l.location}: ${currencyFmt(l.revenue || 0, "USD")}`)
        .join(" • ");
      return {
        left: c._id || "Unknown account",
        right: c.revenue,
        sub: locDetails
      };
    });


    const handleProductRowClick = React.useCallback(
        (row) => {
            const model = row?.meta?.modelNumber || row?.left;
            if (!model) return;
            history.push(`/products?focus=${encodeURIComponent(model)}`);
        },
        [history]
    );


    let paymentHistory = []
    for(let i = 0; i < invoices.length; i++) {
        let history = []
        if(invoices[i].paymentRecords !== undefined) {
            history = [...paymentHistory, invoices[i].paymentRecords]
            paymentHistory = [].concat.apply([], history);
        }
        
    }


    //sort payment history by date
   const sortHistoryByDate =  paymentHistory.sort(function(a, b) {
        var c = new Date(a.datePaid);
        var d = new Date(b.datePaid);
        return d-c;
    });
    
    
    let totalPaid = 0
    let ytdRevenue = 0
    for(let i = 0; i < invoices.length; i++) {
        // if(invoices[i].totalAmountReceived !== undefined) {
        //     totalPaid += invoices[i].totalAmountReceived
        // }
        if (moment(invoices[i].receivedByDate).year() === currYear) {
            ytdRevenue += invoices[i].total
        }
    }

    let totalAmount = 0
    for(let i = 0; i < invoices.length; i++) {
        totalAmount += invoices[i].total
    }
   
    
    useEffect(() => {
        // get by user
        const searchId = authData?.result?._id || authData?.result?.googleId
        if (searchId) {
            dispatch(getInvoicesByUser({search: searchId}));
        }
        // eslint-disable-next-line
    }, [location, dispatch, authData]);
   

    const unpaidInvoice = invoices?.filter((invoice) => invoice.status === 'Unpaid')
    const paid = invoices?.filter((invoice) => invoice.status === 'Paid')
    const partial = invoices?.filter((invoice) => invoice.status === 'Partial')

    let totalOutstanding = 0
    const outstandingInvoice = invoices?.filter((invoice) => invoice.status === 'Unpaid' || invoice.status === 'Partial');
    for(let i = 0; i < outstandingInvoice.length; i++) {
        if(outstandingInvoice[i].totalAmountReceived !== undefined) {
            totalOutstanding += (outstandingInvoice[i].total - outstandingInvoice[i].totalAmountReceived)
        } else {
            totalOutstanding += outstandingInvoice[i].total
        }
        
    }

    if(!authData) {
        history.push('/login')
      }


      if(isLoading) {
        return  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', paddingTop: '20px'}}>
            <Spinner />
        </div>
      }

      if(invoices.length === 0) {
        return  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', paddingTop: '20px'}}>
            {/* <Spinner /> */}
          <Empty />
        <p style={{padding: '40px', color: 'gray'}}>Nothing to display. Click the plus icon to start creating</p>
        </div>
      }

      
    return (
        <div className={styles.pageContainer}>
           
    
            <section className={styles.stat}>
                <h1 style={{textAlign: 'center'}}> Dashboard </h1>
                <Box style={{ display: "grid", gridGap: 16, marginTop: 16, width: "90%", margin: "10px auto" }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <OverdueInvoicesCard currency="USD" autoLoad={false} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <OutstandingInvoicesCard currency="USD" autoLoad={false} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <FlaggedInvoicesCard currency="USD" autoLoad={false} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CustomerInvoicesCard currency="USD" autoLoad={false} />
                        </Grid>
                    </Grid>
                <FiltersBar
                    mode={mode} setMode={setMode}
                    accrualField={accrualField} setAccrualField={setAccrualField}
                    rangePreset={rangePreset} setRangePreset={setRangePreset}
                    useCustom={useCustom} setUseCustom={setUseCustom}
                    fromCustom={fromCustom} setFromCustom={setFromCustom}
                    toCustom={toCustom} setToCustom={setToCustom}
                    statusIn={statusIn} setStatusIn={setStatusIn}
                />

                {/* Optional error banner */}
                {error && (
                    <div style={{padding:12, background:"#fffbeb", border:"1px solid #f59e0b", borderRadius:8, color:"#92400e"}}>
                    {error}
                    </div>
                )}

                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <RevenueTrendChart
                            subheader={`Grouped by ${grp}${error ? " • " + error : ""}`}
                            series={series}
                            currency="USD"
                            totalRevenue={data?.kpis?.gross || 0}
                            mode={mode}
                            accrualField={accrualField}
                            grouping={grp}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <PaymentList
                            entries={timelineEntries}
                            currency="USD"
                            mode={mode}
                            title={mode === "accrual" ? "Accrued Invoices" : "Displayed Payments"}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TaxSummaryTable />
                    </Grid>
                </Grid>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                    <LeaderboardList title={`Top 50 Products`} rows={topProductsRows} currency="USD" onRowClick={handleProductRowClick} maxRows={50} scrollHeight={420} money={topProductMetric !== "quantity"} headerAction={
                        <ButtonGroup size="small" variant="outlined">
                            <Button onClick={() => setTopProductMetric("revenue")} variant={topProductMetric === "revenue" ? "contained" : "outlined"}>Revenue</Button>
                            <Button onClick={() => setTopProductMetric("quantity")} variant={topProductMetric === "quantity" ? "contained" : "outlined"}>Quantity</Button>
                            <Button onClick={() => setTopProductMetric("profit")} variant={topProductMetric === "profit" ? "contained" : "outlined"}>Profit</Button>
                        </ButtonGroup>
                    } />
                    </Grid>
                    <Grid item xs={12} md={6}>
                    <LeaderboardList title="Top 50 Customers" rows={topCustomersRows} currency="USD" maxRows={50} scrollHeight={420} />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <RecentPaymentsCard
                            payments={sortHistoryByDate.slice(0, 20)}
                            currency="USD"
                        />
                    </Grid>
                </Grid>
            </Box>
            </section>
            {/* {paymentHistory.length !== 0 && (
            <section>
                <Chart paymentHistory={paymentHistory} />
            </section>
            )} */}
       
        </div>
    )
}

export default Dashboard
